import { useCallback, useMemo, ReactNode } from 'react';
import {
  GridFilterItem,
  GridPanelContent,
  GridPanelFooter,
  GridPanelHeader,
  GridPanelWrapper,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { Typography, Box, Button } from '@mui/material';
import { UpdatedAlert } from '../../updated-alert';
import { FilterToggleBoolean } from './filter-toggle-boolean';
import { FilterSlider } from './filter-slider';
import { TextToggleContent } from './text-toggle-content';

export function DevicesFilterPanel() {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterMap = useMemo(
    () =>
      new Map(
        filterModel.items.reduce<Array<[string, GridFilterItem]>>((acc, item) => {
          return item.columnField ? [...acc, [item.columnField, item]] : acc;
        }, [])
      ),
    [filterModel.items]
  );

  const handleClearAllFilters = useCallback(
    () => apiRef.current.setFilterModel({ ...filterModel, items: [] }),
    [apiRef, filterModel]
  );

  const applyFilter = useCallback(
    (item: GridFilterItem) => {
      if (item.value != null && item.value !== '') {
        apiRef.current.upsertFilterItem(item);
      } else {
        apiRef.current.deleteFilterItem(item);
      }
    },
    [apiRef]
  );

  const applyBatteryFilter = useCallback(
    (item: GridFilterItem) => {
      if (item.value < 100) {
        apiRef.current.upsertFilterItem(item);
      } else {
        apiRef.current.deleteFilterItem(item);
      }
    },
    [apiRef]
  );

  return (
    <GridPanelWrapper>
      <GridPanelHeader
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography component='span' variant='h5' padding='0 8px'>
          Filters
        </Typography>
      </GridPanelHeader>
      <GridPanelContent sx={{ minWidth: 400 }}>
        <InputGroup>
          <InputLabel>OS</InputLabel>
          <FilterToggleBoolean
            sx={{
              margin: 0,
              flex: '1 0 200px',
              display: 'flex',
            }}
            item={filterMap.get('osUpdated') ?? { columnField: 'osUpdated', value: '' }}
            apiRef={apiRef}
            applyValue={applyFilter}
            allContent={<TextToggleContent text='All' />}
            trueContent={<UpdatedAlert value={true} />}
            falseContent={<UpdatedAlert value={false} />}
          />
        </InputGroup>
        <InputGroup>
          <InputLabel>Project</InputLabel>
          <FilterToggleBoolean
            sx={{
              margin: 0,
              flex: '1 0 200px',
              display: 'flex',
            }}
            item={filterMap.get('projectUpdated') ?? { columnField: 'projectUpdated', value: '' }}
            apiRef={apiRef}
            applyValue={applyFilter}
            allContent={<TextToggleContent text='All' />}
            trueContent={<UpdatedAlert value={true} />}
            falseContent={<UpdatedAlert value={false} />}
          />
        </InputGroup>
        <InputGroup>
          <InputLabel>Apps</InputLabel>
          <FilterToggleBoolean
            sx={{
              margin: 0,
              flex: '1 0 200px',
              display: 'flex',
            }}
            item={filterMap.get('appsUpdated') ?? { columnField: 'appsUpdated', value: '' }}
            apiRef={apiRef}
            applyValue={applyFilter}
            allContent={<TextToggleContent text='All' />}
            trueContent={<UpdatedAlert value={true} />}
            falseContent={<UpdatedAlert value={false} />}
          />
        </InputGroup>
        <InputGroup>
          <InputLabel>Battery</InputLabel>
          <Box
            sx={{
              width: '100%',
              '&>*': {
                marginRight: 2,
                maxWidth: '240px',
              },
            }}
          >
            <FilterSlider
              item={filterMap.get('battery') ?? { columnField: 'battery', value: 100 }}
              apiRef={apiRef}
              applyValue={applyBatteryFilter}
              min={0}
              max={100}
              step={10}
              marks={[
                {
                  value: 0,
                  label: '0',
                },
                {
                  value: 50,
                  label: '50%',
                },
                {
                  value: 100,
                  label: '100%',
                },
              ]}
            />
          </Box>
        </InputGroup>
      </GridPanelContent>
      <GridPanelFooter sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleClearAllFilters}>Clear Filters</Button>
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}

function InputGroup({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        margin: '8px 16px',
      }}
    >
      {children}
    </Box>
  );
}

function InputLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      component='div'
      sx={{
        margin: 0,
        flex: '0 0 100px',
      }}
    >
      {children}
    </Typography>
  );
}
